import { ChangeDetectorRef, Component, OnInit, effect, inject } from '@angular/core';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { AuthService } from './common/services/auth.service';
import { LocalStorageService } from './common/services/local-storage.service';
import { SharedSignalsService } from './common/services/shared-signals.service';
import { Router } from '@angular/router';
import { AnalyticsService } from './common/services/analytics.service';
import { ClarityService } from './common/services/clarity.service';

@Component({
  selector: 'taly-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  private idle = inject(Idle);
  private keepalive = inject(Keepalive);
  public authService = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  public storageService = inject(LocalStorageService);
  private router = inject(Router);
  transferSignal = inject(SharedSignalsService);
  private analyticsService = inject(AnalyticsService);
  private clarityService = inject(ClarityService);

  title: string = 'consumer-ui';
  idleState: string = 'Not started.';
  timedOut: boolean = false;
  isIdleOpen: boolean = false;
  timeOutCount: number;
  lastPing?: Date = null;
  textDirection: string = 'ltr';
  showFooter: boolean = false;
  showHeader: boolean = false;
  headerType: string;
  showSiteHeader: boolean = true;
  isKycPage: boolean = false;

  constructor() {
    effect(() => {
      this.showFooter = this.transferSignal?.showFooter();
      this.showHeader = this.transferSignal?.showHeader();
      this.headerType = this.transferSignal?.headerType();

      this.cdr.detectChanges();

      this.textDirection = this.transferSignal.textDirection();
    });

    //On Load Check Session and start timer

    this.idle.setIdle(5);
    // sets a timeout period of 5 seconds. after 10 seconds of inactivity, the user will be considered timed out.
    this.idle.setTimeout(5);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.onIdleEnd.subscribe(() => (this.idleState = 'No longer idle.'));
    this.idle.onTimeout.subscribe(() => {
      this.idleState = 'Timed out!';
      this.timedOut = true;
      this.transferSignal.setSessionTime(0);
    });

    this.idle.onIdleStart.subscribe(() => {
      this.idleState = "You've gone idle!";
      this.transferSignal.setTimeOutPopup(true);
    });
    this.idle.onTimeoutWarning.subscribe((countdown) => {
      if (countdown > 1) {
        this.transferSignal.setSessionTime(countdown);
      } else {
        this.transferSignal.setSessionTime(0);
      }

      this.idleState = 'You will time out in ' + countdown + ' seconds!';
    });

    this.keepalive.interval(15);

    this.keepalive.onPing.subscribe(() => (this.lastPing = new Date()));
  }

  ngOnInit(): void {
    this.checkOSType();
    this.detectBrowser();
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);

    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    this.isKycPage = currentPath.includes('/kyc');
    const hideHeaderUrls = ['/secure-checkout/payment', '/health', '/maintenance-page', '/kyc'];
    this.showSiteHeader = !hideHeaderUrls.some((path) => currentPath.includes(path));

    this.router.events.subscribe(() => {
      const url = this.router.url;
      this.isKycPage = url.includes('/kyc');
      this.showSiteHeader = !hideHeaderUrls.some((path) => url.includes(path));
    });
  }

  reset() {
    this.idle.stop();
    this.idleState = 'Not started.';
    this.timedOut = false;
    this.lastPing = null;
  }

  idleLogoutHandler() {
    this.idle.watch();
    this.idleState = 'Started.';
    this.timedOut = false;
  }

  checkOSType() {
    const platform = navigator.platform;

    // Check for Android
    if (platform.toLowerCase().includes('android')) {
      this.transferSignal.setUserAgentType('Android');
    }

    // Check for iOS (iPhone/iPad)
    if (
      platform.toLowerCase().includes('mac') ||
      platform.toLowerCase().includes('iphone') ||
      platform.toLowerCase().includes('ipad')
    ) {
      this.transferSignal.setUserAgentType('iOS');
    } else {
      this.transferSignal.setUserAgentType('Unknown');
    }
  }

  detectBrowser() {
    const userAgent = window.navigator.userAgent;
    let browserName: string;

    const isIOSWebView =
      /iPhone|iPad|iPod/.test(userAgent) &&
      !/Safari/.test(userAgent) &&
      !/Macintosh/.test(userAgent);

    if (isIOSWebView) {
      browserName = 'Safari';
    } else if (userAgent.indexOf('Chrome') !== -1) {
      browserName = 'Google Chrome';
    } else if (userAgent.indexOf('Safari') !== -1) {
      browserName = 'Safari';
    } else if (userAgent.indexOf('Firefox') !== -1) {
      browserName = 'Mozilla Firefox';
    } else if (userAgent.indexOf('Edge') !== -1) {
      browserName = 'Microsoft Edge';
    } else if (userAgent.indexOf('MSIE') !== -1 || userAgent.indexOf('Trident/') !== -1) {
      browserName = 'Internet Explorer';
    } else {
      browserName = 'Unknown';
    }

    this.transferSignal.setBrowserName(browserName);
  }
}